import { useState, useCallback, useMemo, useEffect } from 'react';
import chatService from './chatService';
import { errorHandlers } from './serviceUtils';
import { getAgentData } from '../../../services/util';

export const useSearch = (isOpen, isSearchOpen) => {
    const [users, setUsers] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        if (isSearchOpen) {
            search(controller.signal);
        }
        return () => {
            controller.abort();
        }
    }, [isSearchOpen]);

    const search = useCallback(async (signal) => {
        setSearchLoading(true);
        setSearchError(null);
        try {
            const controller = signal ? undefined : new AbortController();
            const searchSignal = signal || (controller?.signal);
            const data = await chatService.search.query(searchSignal);
            if (data && typeof data === 'object' && 'type' in data && 'userList' in data && Array.isArray(data?.userList) && data?.userList?.length > 0) {
                const usersParsed = data.userList.map((user) => ({
                    id: user.subUserId,
                    name: user.subUserName,
                    role: 'admin',
                    status: 'online',
                    parentUserId: user.parentUserId,
                }));
                setUsers(usersParsed);
            } else {
                throw new Error("Invalid response from server");
            }

            return data;
        } catch (err) {
            setSearchError(errorHandlers.getErrorMessage(err));
            return [];
        } finally {
            setSearchLoading(false);
        }
    }, []);

    return {
        users,
        setUsers,
        searchLoading,
        searchError,
        search
    };
};

export const useConversation = (isOpen, isSearchOpen) => {
    const [conversations, setConversations] = useState([]);
    const [activeConversationLoading, setActiveConversationLoading] = useState(false);
    const [activeConversationError, setActiveConversationError] = useState(null);
    const [sendMessageError, setSendMessageError] = useState(null);
    const [sendMessageLoading, setSendMessageLoading] = useState(false);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const activeConversation = useMemo(() => {
        if (conversations && Array.isArray(conversations) && conversations.length > 0) {
            return conversations.find((c) => c.id === activeConversationId) || null;
        }
        return null;
    }, [conversations, activeConversationId]);
    const { users, searchLoading } = useSearch(isOpen, isSearchOpen);

    useEffect(() => {
        if (isOpen) {
            handleUnreadCountOnOpen()
        }
    }, [isOpen]);

    useEffect(() => {
        const controller = new AbortController();
        if (activeConversation) {
            getConversation({
                ...activeConversation,
                unreadCount: isOpen ? 0 : activeConversation?.unreadCount ? activeConversation?.unreadCount + 1 : 1
            }, controller.signal, 'activeConversationChange');
        }
        return () => {
            controller.abort();
        }
    }, [activeConversationId]);

    useEffect(() => {
        const controller = new AbortController();
        getActiveConversations(controller.signal);
        return () => {
            controller.abort();
        }
    }, []);
    async function getActiveConversations(signal) {
        try {
            const response = await chatService.getRecentSubUsersList.send(signal || new AbortController().signal);
            if (response && typeof response === 'object' && 'status' in response && response.status === "success") {
                let parsedResponse = response?.list;
                if (parsedResponse && typeof parsedResponse === 'object' && Array.isArray(parsedResponse) && parsedResponse.length > 0) {
                    setConversations((prev) => {
                        const isPrev = prev && Array.isArray(prev) && prev.length > 0;
                        const prevConversations = isPrev ? [...prev] : [];
                        if (prevConversations && Array.isArray(prevConversations)) {
                            parsedResponse.forEach((conversationFromApi) => {
                                const isExistingConversation = prevConversations.find((c) => c.id === parseInt(conversationFromApi.id));
                                if (!isExistingConversation) {
                                    prevConversations.push({
                                        id: parseInt(conversationFromApi.id),
                                        title: conversationFromApi.name,
                                        participants: [{
                                            id: parseInt(conversationFromApi.id),
                                            name: conversationFromApi.name,
                                            role: 'admin',
                                            status: 'online',
                                        }],
                                        messages: [],
                                        unreadCount: 0,
                                        isGroup: false,
                                        createdAt: new Date(),
                                    });
                                }
                            });
                        }
                        return prevConversations;
                    });
                }
            }
        } catch (err) {
            console.error(err);
        }
    }
    // console.log({
    //     users,
    //     conversations,
    //     activeConversation,
    //     activeConversationId,
    // });

    function handleUnreadCountOnOpen() {
        if (activeConversationId) {
            setConversations((prev) => {
                const isPrev = prev && Array.isArray(prev) && prev.length > 0;
                const isExistingConversation = isPrev ? prev.find((c) => c.id === activeConversationId) : null;
                if (isPrev && isExistingConversation) {
                    return prev.map((c) => {
                        if (c.id === activeConversationId) {
                            return {
                                ...c,
                                unreadCount: 0,
                            };
                        }
                        return c;
                    });
                }
                return prev || [];
            });
        }
    }

    function getParticipants(senderId) {
        console.log("getParticipants", senderId, users, Array.isArray(users), users.length > 0);
        if (users && Array.isArray(users) && users.length > 0) {
            return users.filter((user) => user.id === parseInt(senderId));
        }
        return [];
    }
    const getConversation = useCallback(async (conversation, signal, type) => {
        console.log("getConversation", conversation);
        setActiveConversationLoading(true);
        setActiveConversationError(null);
        let agentData = getAgentData();
        try {
            const conversationId = conversation.id;
            let response = await chatService.conversations.getById(conversationId, signal || new AbortController().signal, conversation?.title || '');
            let messages = [];
            if (response && typeof response === 'object' && 'status' in response && 'hist' in response && response.status === "success" && Array.isArray(response.hist) && response.hist.length > 0) {
                messages = response.hist.map((message, index) => ({
                    id: `msg-${conversationId}-${index}-${new Date(new Date(message.MessageSentTime)).getTime()}`,
                    senderId: message.MessageInitiator === 'Agent' ? agentData?.agentUniqId || '' : conversation.id.toString(),
                    senderName: message.MessageInitiator === 'Agent' ? agentData?.agentInfo?.agentId || '' : conversation.title,
                    content: message.Message,
                    timestamp: new Date(new Date(message.MessageSentTime)),
                    isRead: true,
                    isSent: true,
                }));
            }

            messages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            setConversations((prev) => {
                const isPrev = prev && Array.isArray(prev) && prev.length > 0;
                const isExistingConversation = isPrev ? prev.find((c) => c.id === conversationId) : null;
                if (isPrev && isExistingConversation) {
                    try {
                        return prev.map((c) => {
                            if (c.id === conversationId) {
                                const prevConversation = c;
                                return {
                                    ...prevConversation,
                                    id: conversationId,
                                    title: conversation?.title || prevConversation?.title || conversationId.toString(),
                                    participants: conversation.participants || prevConversation?.participants || [],
                                    messages: messages,
                                    lastMessage: messages[messages.length - 1],
                                    unreadCount: conversation?.unreadCount || 0,
                                    isGroup: false,
                                    createdAt: conversation?.createdAt || prevConversation?.createdAt || new Date(),
                                };
                            }
                            return c;
                        })
                    } catch (err) {
                        console.error(err);
                        return prev;
                    }
                }
                if (!isExistingConversation && !activeConversationId) {
                    let participants = getParticipants(conversationId.toString());
                    if (participants && Array.isArray(participants) && participants.length === 0) {
                        const participant = {
                            id: parseInt(conversationId.toString()),
                            name: conversation?.title || '',
                            role: 'admin',
                            status: 'online',
                        }
                        participants = [participant];
                    }
                    if (participants && Array.isArray(participants) && participants.length > 0) {
                        const newConversation = {
                            id: conversationId,
                            title: conversation?.title || '',
                            messages: messages,
                            participants: participants,
                            createdAt: new Date(),
                            unreadCount: type === 'websocket' ? 1 : 0,
                            isGroup: false,
                        }
                        if (prev?.length && Array.isArray(prev) && prev.length > 0) {
                            return [...prev, newConversation];
                        } else {
                            return [newConversation];
                        }
                    }
                }
                return prev || [];
            });
            if (!activeConversationId && type !== 'websocket') {
                setActiveConversationId(conversationId);
            }
        } catch (err) {
            console.error(err);
            setActiveConversationError(errorHandlers.getErrorMessage(err));
        } finally {
            setActiveConversationLoading(false);
        }
    }, [users]);

    const sendMessage = useCallback(async (message, conversation) => {
        if (!activeConversation) return;
        try {
            setSendMessageLoading(true);
            const agentSendMessagePayload = {
                subUserId: activeConversation?.id || 0,
                customer: activeConversation?.title || '',
                subUserName: activeConversation?.title || '',
                parentUserId: activeConversation?.parentUserId || 0,
                msg: message.content,
            };
            const response = await chatService.messages.send(agentSendMessagePayload);

            if (response && typeof response === 'object' && 'type' in response && response.status === "success") {
                console.log("sent message", response);
            } else {
                throw new Error(response?.Message || 'Failed to send message');
            }

        } catch (err) {
            const updatedConversation = { ...conversation };
            updatedConversation.messages = updatedConversation.messages.filter((m) => m.id !== message.id);
            setActiveConversationId(updatedConversation.id);
            console.error(err);
            setSendMessageError(errorHandlers.getErrorMessage(err));
        } finally {
            setSendMessageLoading(false);
        }
    }, [activeConversation]);

    return {
        activeConversation,
        activeConversationId,
        activeConversationLoading,
        activeConversationError,
        setActiveConversationId,
        getConversation,
        sendMessage,
        conversations,
        setConversations,
        sendMessageError,
        sendMessageLoading,
        getParticipants,
        users,
        searchLoading
    };
};
